import { DEFAULT_LOCALE, directionFor, resources, SUPPORTED_LOCALES } from '@platform/i18n';
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

/**
 * Lingua della console.
 *
 * Segue la preferenza **della persona**, non dell'ente: in un ente altoatesino
 * o svizzero i volontari non parlano tutti la stessa lingua (sezione 4).
 */

const STORAGE_KEY = 'locale';

export function readStoredLocale(): string | undefined {
  try {
    const stored = globalThis.localStorage?.getItem(STORAGE_KEY);
    return stored && (SUPPORTED_LOCALES as readonly string[]).includes(stored) ? stored : undefined;
  } catch {
    return undefined;
  }
}

export function applyDocumentLocale(locale: string): void {
  const root = globalThis.document?.documentElement;
  if (!root) return;
  root.lang = locale;
  // `dir` è calcolato da subito, anche se nessuna lingua RTL è ancora attiva:
  // rimediare dopo costa dieci volte tanto (sezione 4).
  root.dir = directionFor(locale);
}

/**
 * La lingua **non** viene dal browser.
 *
 * La console la prendeva da `navigator.languages`: un volontario italiano con
 * il sistema operativo in inglese — cioè la maggioranza di chi ha comprato il
 * telefono così — apriva la console in inglese a ogni accesso, e la scelta
 * fatta con il selettore non sopravviveva a una finestra privata
 * (`docs/DECISIONI.md`, AQ2).
 *
 * L'ordine adesso è quello che la sezione 4 descrive: la scelta di **questa**
 * persona su questo dispositivo, poi la lingua del suo profilo, e in ultimo la
 * lingua predefinita. Il browser non entra: dice come si legge il web, non in
 * che lingua si risponde al telefono per un ente.
 */
export function initialLocale(): string {
  return readStoredLocale() ?? DEFAULT_LOCALE;
}

export async function setupI18n(locale = initialLocale()) {
  await i18next.use(initReactI18next).init({
    lng: locale,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...SUPPORTED_LOCALES],
    defaultNS: 'dashboard',
    ns: ['dashboard', 'errors', 'emergency'],
    resources: Object.fromEntries(
      SUPPORTED_LOCALES.map((supported) => [supported, resources[supported]]),
    ),
    interpolation: { escapeValue: false },
  });
  applyDocumentLocale(locale);
  return i18next;
}

export async function changeLocale(locale: string): Promise<void> {
  await i18next.changeLanguage(locale);
  applyDocumentLocale(locale);
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, locale);
  } catch {
    // Se il browser non permette di ricordare la scelta, la lingua vale per
    // questa sessione. Non è un errore da mostrare.
  }
}

/**
 * La lingua del profilo, quando questo dispositivo non ne ha una sua.
 *
 * Si sa solo dopo l'accesso — prima non si sa chi sta guardando — e non
 * scavalca mai una scelta fatta qui: chi ha toccato il selettore su questo
 * telefono ha detto l'ultima parola, e un profilo creato in un'altra lingua
 * non gliela toglie.
 */
export async function applyPreferredLocale(locale: string | null | undefined): Promise<void> {
  if (!locale || readStoredLocale()) return;
  if (!(SUPPORTED_LOCALES as readonly string[]).includes(locale)) return;
  if (i18next.resolvedLanguage === locale) return;
  await i18next.changeLanguage(locale);
  applyDocumentLocale(locale);
}

export { i18next };
